import _ from 'lodash';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { twMerge } from 'tailwind-merge';
import deviceStore from '@client/stores/deviceStore';
import modalStore from '@client/stores/modalStore';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import { Modal as SharedModal } from '@client/component/modal/Modal';
import AlertModal from '@client/component/modal/AlertModal';
import ConfirmModal from '@client/component/modal/ConfirmModal';
import CustomModal from '@client/component/modal/CustomModal';
import MenuModal from '@client/component/modal/MenuModal';
import SelectModal from '@client/component/modal/SelectModal';
import ErrorModal from '@client/component/modal/ErrorModal';
import { ModalActionButton, ModalCloseXButton } from '@client/component/modal/ModalButton';
import Carousel, { CarouselProvider, useCarouselContext } from '../Carousel';
import LoginModal from './LoginModal';
import GuideModal from './GuideModal';
import StockModal from './StockModal';

/**
 * InternalModal - haewao 전용 라우터
 * - 공유 타입 + haewao 전용 타입(login, guide, stock) 처리
 */
const InternalModal = observer(({ modalId }: { modalId: string }) => {
    const modalItem = modalStore.modalMap.get(modalId)?.modalItem;

    if (!modalItem) {
        return null;
    }

    switch (modalItem.option.type) {
        case 'alert':
            return <AlertModal modalId={modalId} option={modalItem.option} />;
        case 'confirm':
            return <ConfirmModal modalId={modalId} option={modalItem.option} />;
        case 'login':
            return <LoginModal modalId={modalId} option={modalItem.option} />;
        case 'guide':
            return <GuideModal modalId={modalId} option={modalItem.option} />;
        case 'custom':
            return <CustomModal modalId={modalId} option={modalItem.option} />;
        case 'select':
            return <SelectModal modalId={modalId} option={modalItem.option} />;
        case 'menu':
            return <MenuModal modalId={modalId} option={modalItem.option} />;
        case 'stock':
            return <StockModal modalId={modalId} option={modalItem.option} />;
        case 'error':
            return <ErrorModal modalId={modalId} option={modalItem.option} />;
        default:
            return null;
    }
});

/**
 * 모달 컨테이너 (haewao 전용)
 * - 공유 타입(alert, confirm, custom, select, menu, error) 외에
 *   login, guide, stock 타입도 처리
 * - haewao Layout에서 사용
 */
export const ModalContainer = observer(() => {
    return (
        <div className="invisible fixed h-0 w-0">
            {_.map(Array.from(modalStore.modalMap.entries()), ([modalId, obj]) => {
                const modalItem = obj.modalItem;

                if (modalItem?.state === 'opened' || modalItem?.state === 'closing') {
                    return <InternalModal key={modalId} modalId={modalId} />;
                }
                return null;
            })}
        </div>
    );
});

/**
 * BodyCarousel용 ViewStore
 */
class BodyCarouselViewStore {
    isHovered = false;

    constructor() {
        makeAutoObservable(this);
    }

    setHovered(hovered: boolean) {
        this.isHovered = hovered;
    }
}

/**
 * ModalCarousel - 캐러셀 레이아웃을 가진 모달
 * - CarouselProvider를 자동으로 제공
 * - 스크롤 영역과 좌/우 버튼, Dots를 포함
 * - children으로 Carousel.Slide들을 받음
 */
const ModalCarousel = observer(
    ({ children, className }: { children: React.ReactNode; className?: string }) => {
        const viewStore = useMobxInstance(BodyCarouselViewStore);

        return (
            <CarouselProvider autoHeight={true}>
                <div
                    className={twMerge('relative', className)}
                    onMouseEnter={() => {
                        viewStore.setHovered(true);
                    }}
                    onMouseLeave={() => {
                        viewStore.setHovered(false);
                    }}
                >
                    <div
                        className="max-h-[calc(90vh-8rem)] overflow-auto px-0"
                        style={{
                            WebkitOverflowScrolling: 'touch',
                            overscrollBehaviorY: 'contain',
                        }}
                    >
                        <Carousel>{children}</Carousel>
                    </div>

                    {deviceStore.hasHoverCapability && (
                        <Carousel.PrevCircleButton
                            isShow={viewStore.isHovered}
                            className="absolute top-1/2 -left-5.5 -translate-y-1/2"
                        />
                    )}

                    {deviceStore.hasHoverCapability && (
                        <Carousel.NextCircleButton
                            isShow={viewStore.isHovered}
                            className="absolute top-1/2 -right-5.5 -translate-y-1/2"
                        />
                    )}

                    <div className="mt-4 px-5">
                        <Carousel.Dots />
                    </div>
                </div>
            </CarouselProvider>
        );
    },
);

const ModalCarouselContainer = observer(
    ({ children, className }: { children: React.ReactNode; className?: string }) => {
        const { emblaApi } = useCarouselContext();

        return (
            <Carousel.Container className={twMerge('gap-5', className)}>
                {children}
            </Carousel.Container>
        );
    },
);

/**
 * Modal 컴포넌트 - haewao 확장 버전
 * - 공유 Modal에 ModalCarousel, ModalCarouselContainer 추가
 */
export const Modal = Object.assign({}, SharedModal, {
    ModalCarousel,
    ModalCarouselContainer,
});
